/**
 * The operator label vocabulary shared by the mail desk and the call desk.
 *
 * A thread and a call store only label ids; the name and the colour live in one
 * `crm.labels.*` Setting row per desk, so renaming a label does not rewrite a
 * single row. Mirrors `apps/api/src/modules/settings/crm-labels.settings.ts` -
 * the tone list and the ceiling have to agree with the server, which normalizes
 * anything it does not recognise.
 */
import { api } from "./api";

export type LabelScope = "mail" | "calls";

export const LABEL_TONES = ["primary", "primary2", "success", "warning", "danger", "neutral"] as const;
export type LabelTone = (typeof LABEL_TONES)[number];

export interface CrmLabel {
  id: string;
  name: string;
  tone: LabelTone;
}

export interface CrmLabelSets {
  mail: CrmLabel[];
  calls: CrmLabel[];
}

/** Same ceiling as the server; the editor stops offering "add" at this point. */
export const MAX_LABELS = 16;

export const EMPTY_LABEL_SETS: CrmLabelSets = { mail: [], calls: [] };

/**
 * Tone to a CSS colour. Deliberately the design tokens rather than fixed hex:
 * these are the same variables the campaign dots already use, so a label reads
 * the same in both themes without a second palette to keep in step.
 */
const TONE_VAR: Record<LabelTone, string> = {
  primary: "var(--color-primary)",
  primary2: "var(--color-primary-2)",
  success: "var(--color-success)",
  warning: "var(--color-warning)",
  danger: "var(--color-destructive)",
  neutral: "var(--color-muted-foreground)",
};

export function labelColor(tone: LabelTone): string {
  return TONE_VAR[tone] ?? TONE_VAR.neutral;
}

/**
 * Add or remove one label, keeping only ids the registry still knows.
 *
 * The filtering is the point. A row can be carrying an id whose label was
 * deleted from the registry weeks ago; sending it back would be asking the
 * server to store a label that no longer exists, and the server would drop it
 * anyway. Touching a row's labels is therefore also when its leftovers go.
 */
export function toggleTag(current: readonly string[], known: readonly CrmLabel[], id: string): string[] {
  const ids = new Set(known.map((l) => l.id));
  const kept = current.filter((tag) => ids.has(tag) && tag !== id);
  return current.includes(id) ? kept : [...kept, id];
}

/** The labels a row actually has, in registry order so two rows read alike. */
export function labelsOf(known: readonly CrmLabel[], tags: readonly string[] | undefined): CrmLabel[] {
  if (!tags?.length) return [];
  return known.filter((l) => tags.includes(l.id));
}

export const getCrmLabels = () => api.get<CrmLabelSets>("/admin/crm/labels");

export const saveCrmLabels = (scope: LabelScope, labels: { id?: string; name: string; tone: LabelTone }[]) =>
  api.put<{ labels: CrmLabel[] }>(`/admin/crm/labels/${scope}`, { labels });
